import React, { useState, useEffect } from 'react';
import Canvas from '../Canvas';
import ResultsPanel from '../ResultsPanel';
import BottomSheet from './BottomSheet';
import { movableSections } from '../../partCatalog';
import { ventOptions, findVentOption } from '../../data';
import { ventTypeLabel, ventTypePlaceholder } from '../../ventTypeLabels';

// Per-part editor used inside the phone "Edit part" sheet. Reuses the exact same
// handlers the desktop Output panel uses, so editing behaves identically — only
// the presentation (full-width sheet, ± length stepper) differs.
function PartEditor({
  item,
  index,
  availableOrientations,
  handleVentTypeChange,
  handleDiametersChange,
  handleHeightChange,
  handleAmbientTempChange,
  handleOrientationChange,
  handleSequenceOrderChange,
  handleDelete,
  onConfirm,
}) {
  const ct = item.componentType;
  const isStraight =
    ct === 'pipe-straight' || ct === 'chimney-straight' || ct === 'masonry-straight';
  const canEditHeight = item.type === 'Pipe';
  const canEditVentType = isStraight;

  const stovePipeOptions =
    ct === 'pipe-straight' ? ventOptions.filter((v) => v.category === 'stove_pipe') : [];
  const chimneyOptions =
    ct === 'chimney-straight' ? ventOptions.filter((v) => v.category === 'chimney') : [];
  const masonryOptions =
    ct === 'masonry-straight' ? ventOptions.filter((v) => v.category === 'masonry') : [];
  const selectedVentOption = item.ventOptionId ? findVentOption(item.ventOptionId) : null;

  const curOrient = item.orientation || 'vertical';
  const nextOrient = curOrient === 'vertical' ? 'horizontal' : 'vertical';
  const curLabel =
    (availableOrientations.find((o) => o.value === curOrient) || { label: curOrient }).label;

  const len = Number(item.length ?? item.height ?? 3);
  const stepLen = (delta) =>
    handleHeightChange(index, String(Math.max(0.5, Math.round((len + delta) * 4) / 4)));

  return (
    <div className="m-editor">
      {canEditVentType && (
        <div className="m-field">
          <label>{ventTypeLabel(item.componentType)}</label>
          <select
            value={item.ventOptionId || ''}
            onChange={(e) => handleVentTypeChange(index, e.target.value)}
          >
            <option value="">{ventTypePlaceholder(item.componentType)}</option>
            {stovePipeOptions.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
            {chimneyOptions.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
            {masonryOptions.map((v) => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
          </select>
        </div>
      )}

      {selectedVentOption && <div className="m-subtle">{selectedVentOption.description}</div>}

      <div className="m-field">
        <label>Diameter</label>
        <select
          value={item.selectedDiameter || item.diameters?.[0] || '6'}
          onChange={(e) => handleDiametersChange(index, e.target.selectedOptions)}
        >
          {['3', '4', '5', '6', '7', '8'].map((d) => (
            <option key={d} value={d}>{d} in</option>
          ))}
        </select>
      </div>

      {canEditHeight && (
        <div className="m-field">
          <label>Length (ft)</label>
          <div className="m-stepper">
            <button type="button" onClick={() => stepLen(-1)} aria-label="Decrease length">−</button>
            <input
              type="number"
              min="0.5"
              step="0.25"
              value={len}
              onChange={(e) => handleHeightChange(index, e.target.value)}
            />
            <button type="button" onClick={() => stepLen(1)} aria-label="Increase length">＋</button>
          </div>
        </div>
      )}

      {canEditVentType && (
        <div className="m-field">
          <label>Orientation</label>
          <button
            type="button"
            className="m-btn"
            onClick={() => handleOrientationChange(index, nextOrient)}
            title="Rotate 90°"
          >
            ↻ {curLabel}
          </button>
        </div>
      )}

      <div className="m-field">
        <label>Surrounding Temp (°F)</label>
        <input
          type="number"
          value={item.ambientTempF ?? ''}
          onChange={(e) => handleAmbientTempChange(index, e.target.value)}
        />
      </div>

      <div className="m-field">
        <label>Part Number</label>
        <input
          type="number"
          min="1"
          step="1"
          value={item.sequenceOrder ?? ''}
          onChange={(e) => handleSequenceOrderChange(index, e.target.value)}
        />
      </div>

      <button type="button" className="m-btn primary" onClick={onConfirm}>
        Confirm
      </button>
      <button type="button" className="m-btn danger" onClick={() => handleDelete(index)}>
        Delete part
      </button>
    </div>
  );
}

// The phone workspace: a hero canvas with a bottom action bar (Add / Setup /
// Run, plus Results once a sim has run) and slide-up sheets. Reuses the same
// Canvas, ResultsPanel, part catalog, and App handlers as the desktop layout.
export default function MobileWorkspace(props) {
  const {
    // canvas
    canvasItems, setCanvasItems, selectedItemIndex, setSelectedItemIndex,
    showStoveMenu, setShowStoveMenu, doorAngle, setDoorAngle,
    indoorTempF, setIndoorTempF, outdoorTempF, setOutdoorTempF,
    elevationFt, setElevationFt, stoveX, setStoveX, setStoveY,
    roofOutdoorOverhang, setRoofOutdoorOverhang, wallTopY, setWallTopY,
    // adding / actions
    onAddPart, onFinalizeDesign, finalizeDisabled, handleClearDesign,
    // setup sheet
    selectedFireProfile, fireProfileOptions, onFireProfileChange,
    customProfileName, onEditCustomProfile,
    depressurizationPa, setDepressurizationPa,
    templateOptions, onInsertTemplate,
    // status
    outputError, outputStatus,
    // results
    hasResults, showResults, setShowResults, lastResult, resultUnits, setResultUnits,
    // part editor handlers
    handleVentTypeChange, handleDiametersChange, handleHeightChange,
    handleAmbientTempChange, handleOrientationChange, availableOrientations,
    handleSequenceOrderChange, handleDelete,
  } = props;

  const [sheet, setSheet] = useState(null); // 'add' | 'setup' | 'props' | 'results' | null
  const [selectedTemplate, setSelectedTemplate] = useState(templateOptions[0]?.value || '');

  const hasSelection = selectedItemIndex !== null && selectedItemIndex !== undefined;

  // Selecting a part opens the Properties sheet; deselecting closes it.
  useEffect(() => {
    if (hasSelection) setSheet('props');
    else setSheet((s) => (s === 'props' ? null : s));
  }, [selectedItemIndex, hasSelection]);

  // A finished simulation opens Results; editing (which clears results) closes it.
  useEffect(() => {
    if (showResults && lastResult) setSheet('results');
    else setSheet((s) => (s === 'results' ? null : s));
  }, [showResults, lastResult]);

  const closeProps = () => { setSelectedItemIndex(null); setSheet(null); };
  const closeResults = () => { setShowResults(false); setSheet(null); };

  const selectedItem = hasSelection ? canvasItems[selectedItemIndex] : null;

  return (
    <div className="m-workspace">
      <div className="m-canvas-host">
        <Canvas
          isMobile
          fullWidth
          hideTitle
          items={canvasItems}
          setItems={setCanvasItems}
          selectedItemIndex={selectedItemIndex}
          setSelectedItemIndex={setSelectedItemIndex}
          showStoveMenu={showStoveMenu}
          setShowStoveMenu={setShowStoveMenu}
          doorAngle={doorAngle}
          setDoorAngle={setDoorAngle}
          indoorTempF={indoorTempF}
          setIndoorTempF={setIndoorTempF}
          outdoorTempF={outdoorTempF}
          setOutdoorTempF={setOutdoorTempF}
          elevationFt={elevationFt}
          setElevationFt={setElevationFt}
          stoveX={stoveX}
          onStoveXChange={setStoveX}
          onStoveYChange={setStoveY}
          roofOutdoorOverhang={roofOutdoorOverhang}
          onRoofOutdoorOverhangChange={setRoofOutdoorOverhang}
          wallTopY={wallTopY}
          onWallTopYChange={setWallTopY}
        />
      </div>

      {(outputError || outputStatus) && (
        <div className={`m-banner${outputError ? ' error' : ''}`}>
          {outputStatus && <div>{outputStatus}</div>}
          {outputError && <pre>{outputError}</pre>}
        </div>
      )}

      <div className="m-action-bar">
        <button type="button" className="m-action-btn" onClick={() => setSheet('add')}>＋ Add</button>
        <button type="button" className="m-action-btn" onClick={() => setSheet('setup')}>Setup</button>
        {hasResults && (
          <button
            type="button"
            className="m-action-btn results"
            onClick={() => { setShowResults(true); setSheet('results'); }}
          >
            Results
          </button>
        )}
        <button
          type="button"
          className="m-action-btn run"
          onClick={onFinalizeDesign}
          disabled={finalizeDisabled}
        >
          Run ▸
        </button>
      </div>

      {/* Add a part */}
      <BottomSheet open={sheet === 'add'} title="Add a part" onClose={() => setSheet(null)}>
        {movableSections.map((section) => {
          const noInvert =
            section.title === 'Stove Pipes' || section.title === 'Lined Masonry Chimneys';
          return (
            <div key={section.title} className="m-add-section">
              <div className="m-add-cat">{section.title}</div>
              <div className="m-add-grid">
                {section.components.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    className="m-add-item"
                    onClick={() => { onAddPart(c.item); setSheet(null); }}
                  >
                    <img
                      src={c.svgSrc}
                      alt={c.label}
                      className={`m-add-img${noInvert ? '' : ' invert'}`}
                    />
                    <span>{c.label}</span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <p className="m-hint">Tap a part to drop it on the canvas, then drag it into place.</p>
      </BottomSheet>

      {/* Setup */}
      <BottomSheet open={sheet === 'setup'} title="Setup" onClose={() => setSheet(null)}>
        <div className="m-field">
          <label>Elevation (ft)</label>
          <input type="number" value={elevationFt} onChange={(e) => setElevationFt(e.target.value)} />
        </div>
        <div className="m-field">
          <label>Room Depressurization (Pa)</label>
          <input
            type="number"
            value={depressurizationPa}
            onChange={(e) => setDepressurizationPa(e.target.value)}
          />
        </div>
        <div className="m-field">
          <label>Fire Profile</label>
          <select value={selectedFireProfile} onChange={(e) => onFireProfileChange(e.target.value)}>
            {fireProfileOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        {customProfileName && (
          <div className="m-subtle">
            Loaded: {customProfileName}{' '}
            <button type="button" className="m-link" onClick={onEditCustomProfile}>Edit</button>
          </div>
        )}
        <div className="m-field">
          <label>Template</label>
          <select value={selectedTemplate} onChange={(e) => setSelectedTemplate(e.target.value)}>
            {templateOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="m-btn primary"
          disabled={!selectedTemplate}
          onClick={() => { onInsertTemplate(selectedTemplate); setSheet(null); }}
        >
          Insert template
        </button>
        <button
          type="button"
          className="m-btn danger"
          disabled={canvasItems.length === 0}
          onClick={() => { handleClearDesign(); setSheet(null); }}
        >
          Clear design
        </button>
      </BottomSheet>

      {/* Edit selected part */}
      <BottomSheet open={sheet === 'props' && !!selectedItem} title="Edit part" onClose={closeProps}>
        {selectedItem && (
          <PartEditor
            item={selectedItem}
            index={selectedItemIndex}
            availableOrientations={availableOrientations}
            handleVentTypeChange={handleVentTypeChange}
            handleDiametersChange={handleDiametersChange}
            handleHeightChange={handleHeightChange}
            handleAmbientTempChange={handleAmbientTempChange}
            handleOrientationChange={handleOrientationChange}
            handleSequenceOrderChange={handleSequenceOrderChange}
            handleDelete={(i) => { handleDelete(i); setSheet(null); }}
            onConfirm={closeProps}
          />
        )}
      </BottomSheet>

      {/* Results */}
      <BottomSheet open={sheet === 'results'} title="Results" onClose={closeResults} height="92vh">
        <ResultsPanel
          result={lastResult}
          units={resultUnits}
          onUnitsChange={setResultUnits}
          depressurizationPa={Math.abs(Number(depressurizationPa) || 0)}
        />
      </BottomSheet>
    </div>
  );
}
